import { SidebarNavigationItem, sidebarNavigation } from '../sider/sidebarNavigation';

export interface BreadcrumbItem {
  title: string;
  key: string;
  /** Đường dẫn tuyệt đối; bỏ trống nếu chỉ hiển thị (không điều hướng được). */
  url?: string;
}

/** Hậu tố của route danh sách/tạo mới — cần bỏ đi để nhận diện "khu vực" của trang chi tiết. */
const SECTION_SUFFIXES = ['/list', '/new'] as const;

/** Nhãn cho phần đuôi của URL không có trong menu (trang chi tiết, tạo mới, chỉnh sửa). */
const LEAF_LABELS: Record<string, string> = {
  new: 'Tạo mới',
  edit: 'Chỉnh sửa',
};

const toAbsolutePath = (url: string): string => (url.startsWith('/') ? url : `/${url}`);

const withoutTrailingSlash = (path: string): string =>
  path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

/** Bỏ hậu tố `/list` hoặc `/new` để lấy đường dẫn gốc của khu vực. */
const sectionPath = (path: string): string => {
  const suffix = SECTION_SUFFIXES.find((item) => path.endsWith(item));
  return suffix ? path.slice(0, -suffix.length) : path;
};

interface NavCandidate {
  parent?: SidebarNavigationItem;
  item: SidebarNavigationItem;
  path: string;
  section: string;
  isListRoute: boolean;
}

const buildCandidates = (navigation: readonly SidebarNavigationItem[]): NavCandidate[] =>
  navigation.flatMap((item) => {
    const children = item.children ?? [];

    const own: NavCandidate[] =
      !item.children && item.url
        ? [
            {
              item,
              path: withoutTrailingSlash(toAbsolutePath(item.url)),
              section: withoutTrailingSlash(sectionPath(toAbsolutePath(item.url))),
              isListRoute: toAbsolutePath(item.url).endsWith('/list'),
            },
          ]
        : [];

    const fromChildren: NavCandidate[] = children
      .filter((child) => Boolean(child.url))
      .map((child) => {
        const path = withoutTrailingSlash(toAbsolutePath(child.url as string));
        return {
          parent: item,
          item: child,
          path,
          section: withoutTrailingSlash(sectionPath(path)),
          isListRoute: path.endsWith('/list'),
        };
      });

    return [...own, ...fromChildren];
  });

const isUnder = (pathname: string, base: string): boolean =>
  base !== '/' && (pathname === base || pathname.startsWith(`${base}/`));

/**
 * Chọn mục menu khớp nhất với URL hiện tại.
 * Ưu tiên: khớp tuyệt đối > khớp theo đường dẫn menu > khớp theo khu vực (ưu tiên route `/list`, đường dẫn dài hơn).
 */
const findCandidate = (pathname: string, candidates: readonly NavCandidate[]): NavCandidate | undefined => {
  const exact = candidates.find((candidate) => candidate.path === pathname);
  if (exact) return exact;

  const byPath = candidates
    .filter((candidate) => isUnder(pathname, candidate.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
  if (byPath) return byPath;

  return candidates
    .filter((candidate) => isUnder(pathname, candidate.section))
    .sort((a, b) => b.section.length - a.section.length || Number(b.isListRoute) - Number(a.isListRoute))[0];
};

/**
 * Nhãn cho các đoạn URL còn lại sau khi đã khớp menu.
 * Vd: `/inventory/products/77` -> "Chi tiết #77" (hoặc mã nghiệp vụ nếu trang có đăng ký),
 * `/orders/12/edit` -> "DH000012 › Chỉnh sửa".
 */
const leafCrumbs = (restSegments: readonly string[], base: string, leafLabel?: string): BreadcrumbItem[] => {
  if (restSegments.length === 0) return [];

  const last = restSegments[restSegments.length - 1];
  const id = restSegments.find((segment) => /^\d+$/.test(segment));
  const detailTitle = leafLabel || (id ? `Chi tiết #${id}` : undefined);
  const actionLabel = LEAF_LABELS[last];

  if (!actionLabel) return [{ title: detailTitle ?? last, key: `leaf-${restSegments.join('-')}` }];

  return [
    // Trang tạo mới/chỉnh sửa vẫn giữ được cấp bản ghi cha nếu biết mã của nó.
    ...(id && leafLabel ? [{ title: leafLabel, key: `leaf-${id}`, url: `${base}/${id}` }] : []),
    { title: actionLabel, key: `leaf-${last}` },
  ];
};

export interface BuildBreadcrumbsOptions {
  /** Nhãn thay thế cho breadcrumb cuối, vd: mã sản phẩm "SP00077". */
  leafLabel?: string;
  navigation?: readonly SidebarNavigationItem[];
}

/**
 * Dựng breadcrumb cho một URL bất kỳ, kể cả các trang không có trong menu
 * (chi tiết `/inventory/products/77`, chỉnh sửa `/orders/12/edit`, ...).
 */
export const buildBreadcrumbs = (
  pathname: string,
  { leafLabel, navigation = sidebarNavigation }: BuildBreadcrumbsOptions = {},
): BreadcrumbItem[] => {
  const normalized = withoutTrailingSlash(pathname || '/');

  if (normalized === '/') {
    const root = navigation.find((item) => item.url === '/');
    return root ? [{ title: root.title, key: 'root', url: '/' }] : [];
  }

  const candidates = buildCandidates(navigation);
  const matched = findCandidate(normalized, candidates);
  if (!matched) return [];

  const crumbs: BreadcrumbItem[] = [
    // Mục cha chỉ để thể hiện cấp bậc, không điều hướng được.
    ...(matched.parent ? [{ title: matched.parent.title, key: matched.parent.key }] : []),
    { title: matched.item.title, key: matched.item.key, url: matched.path },
  ];

  const base = matched.path === normalized ? matched.path : matched.section;
  const rest = normalized
    .slice(base.length)
    .split('/')
    .filter((segment) => segment);
  return [...crumbs, ...leafCrumbs(rest, base, leafLabel)];
};
